import type { AdminKycCheck, KycCheckStatus } from '../../api/types';
import type { StatusIconName } from '../ui/StatusIcon';
import { formatIstDayMon } from '../../features/payments/paymentDates';

type Tone = 'green' | 'amber' | 'red' | 'gray';

export interface KycCheckPill {
  bg: string;
  text: string;
  label: string;
  icon: StatusIconName | undefined;
}

export interface KycCheckDisplay {
  /** The result pill, plus "Aadhaar not linked · 20% TDS" for an inoperative PAN (R28). */
  pills: KycCheckPill[];
  /** "<reason> · by <actor> · DD Mon YYYY" under an ops override; null otherwise. */
  detail: string | null;
  /** Contract C6: only MISMATCH, INVALID, ERROR and NOT_CHECKED may be overridden. */
  canOverride: boolean;
}

const OVERRIDABLE: readonly KycCheckStatus[] = ['MISMATCH', 'INVALID', 'ERROR', 'NOT_CHECKED'];

const pill = (tone: Tone, label: string, icon: StatusIconName | undefined): KycCheckPill => ({
  bg: `var(--status-${tone}-bg)`,
  text: `var(--status-${tone}-text)`,
  label,
  icon,
});

function resultWords(c: AdminKycCheck): { tone: Tone; icon: StatusIconName; label: string } | null {
  const pan = c.checkType === 'PAN';
  const score = c.matchScore != null ? `${c.matchScore}%` : null;
  switch (c.status) {
    case 'VERIFIED':
      return {
        tone: 'green',
        icon: 'check',
        label: `${pan ? 'PAN' : 'Bank'} verified${score ? ` · name match ${score}` : ''}`,
      };
    case 'MISMATCH':
      return {
        tone: 'amber',
        icon: 'schedule',
        label: pan
          ? `PAN name mismatch${score ? ` · ${score}` : ''}`
          : c.nameReturned
            ? `Bank mismatch: holder '${c.nameReturned}'`
            : 'Bank name mismatch',
      };
    case 'INVALID':
      return { tone: 'red', icon: 'close', label: pan ? 'PAN not found' : 'Bank account not found' };
    case 'ERROR':
      return { tone: 'amber', icon: 'history', label: 'Check failed · review manually' };
    case 'NOT_CHECKED':
      return { tone: 'gray', icon: 'hourglass', label: 'Not checked' };
    default:
      return null;
  }
}

/**
 * Words for one PAN / bank check, mapped 1:1 from the API (Contract C6). Nothing is computed:
 * scores and names are shown as the API sends them. Badges inform ops and gate nothing (R28).
 */
export function kycCheckDisplay(c: AdminKycCheck): KycCheckDisplay {
  const pills: KycCheckPill[] = [];
  let detail: string | null = null;
  if (c.provider === 'OPS_OVERRIDE') {
    // The reason (up to 500 characters) goes on the wrapping detail line, not in the pill.
    pills.push(pill('green', 'Overridden', 'check'));
    const parts = [
      c.overrideReason,
      c.actor ? `by ${c.actor}` : null,
      c.checkedAt ? formatIstDayMon(c.checkedAt) : null,
    ];
    detail = parts.filter(Boolean).join(' · ') || null;
  } else {
    const words = resultWords(c);
    if (words) {
      pills.push(
        c.stale
          ? pill('gray', `${words.label} · out of date`, words.icon)
          : pill(words.tone, words.label, words.icon),
      );
    } else {
      const w = String(c.status ?? '').replace(/_/g, ' ').trim().toLowerCase();
      pills.push(pill('gray', w ? w[0].toUpperCase() + w.slice(1) : 'Unknown', undefined));
    }
  }
  if (c.checkType === 'PAN' && c.aadhaarLinked === false) {
    pills.push(pill('amber', 'Aadhaar not linked · 20% TDS', 'schedule'));
  }
  return {
    pills,
    detail,
    canOverride: c.provider !== 'OPS_OVERRIDE' && OVERRIDABLE.includes(c.status),
  };
}
